import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  format,
  addDays,
  startOfWeek,
  getISOWeek,
  isSameDay,
} from "date-fns";
import { sv } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CalendarDays,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useLeagueCalendarData,
  type CalendarMatch,
  type _CalendarBooking,
} from "@/hooks/useLeagueCalendarData";
import { calculateEveningScrollPosition } from "@/lib/leagueCalendarScroll";
import { useLeagueZones } from "@/hooks/useLeagueZones";
import type { LeagueAvailabilityZone } from "@/hooks/useLeagueZones";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

// ── Constants ──────────────────────────────────────────────────────────

const DAY_LABEL_KEYS = ["leagues.calendar.mon", "leagues.calendar.tue", "leagues.calendar.wed", "leagues.calendar.thu", "leagues.calendar.fri", "leagues.calendar.sat", "leagues.calendar.sun"];
const COURT_LABEL_W = 120;
const SLOT_W = 48;
const SLOT_H = 56;

function getDivisionColor(level: number): { bg: string; hex: string; label: string } {
  const NAMED = [
    { bg: 'bg-blue-500', hex: '#3B82F6', label: 'blue' },
    { bg: 'bg-green-500', hex: '#22C55E', label: 'green' },
    { bg: 'bg-amber-500', hex: '#F59E0B', label: 'amber' },
    { bg: 'bg-purple-500', hex: '#A855F7', label: 'purple' },
    { bg: 'bg-cyan-500', hex: '#06B6D4', label: 'cyan' },
  ];
  if (level >= 1 && level <= NAMED.length) return NAMED[level - 1];
  const hue = ((level - 1) * 137.5) % 360;
  return { bg: '', hex: `hsl(${hue}, 70%, 50%)`, label: '' };
}

// ── Props ──────────────────────────────────────────────────────────────

interface LeagueCalendarProps {
  leagueId: string;
  courts: Array<{ id: string; name: string }>;
  hallHours: { start: number; end: number };
  matchDuration: number;
  readOnly?: boolean;
  enableZoneMode?: boolean;
  initialDate?: string;
}

// ── Helpers ────────────────────────────────────────────────────────────

function timeToMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + (m || 0);
}

function minutesToTime(m: number): string {
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

// ── Component ──────────────────────────────────────────────────────────

export default function LeagueCalendar({
  leagueId,
  courts,
  hallHours,
  matchDuration,
  readOnly = false,
  enableZoneMode = false,
  initialDate,
}: LeagueCalendarProps) {
  const { t } = useTranslation("admin");
  const queryClient = useQueryClient();

  // State
  const [weekStart, setWeekStart] = useState(() =>
    startOfWeek(initialDate ? new Date(initialDate) : new Date(), { weekStartsOn: 1 }),
  );
  const [selectedDayIdx, setSelectedDayIdx] = useState(() => {
    const d = new Date().getDay();
    return d === 0 ? 6 : d - 1;
  });
  const [divisionFilter, setDivisionFilter] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'week' | 'day'>('week');
  const [unplacedOpen, setUnplacedOpen] = useState(true);
  const [calendarMode, setCalendarMode] = useState<'schedule' | 'zones'>('schedule');

  // Zone mutations (only used when enableZoneMode)
  const { createZone } = useLeagueZones(enableZoneMode ? leagueId : undefined);

  // Data
  const {
    bookings,
    weekMatches,
    unplacedMatches,
    divisions,
    zones,
    placeMatch,
    unplaceMatch,
    isLoading,
  } = useLeagueCalendarData(leagueId, weekStart);

  // Derived
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );
  const currentDate = weekDays[selectedDayIdx];
  const dateStr = format(currentDate, "yyyy-MM-dd");
  const weekNum = getISOWeek(weekStart);

  // Time slots (30-min intervals)
  const timeSlots = useMemo(() => {
    const arr: string[] = [];
    for (let h = hallHours.start; h < hallHours.end; h++) {
      arr.push(`${String(h).padStart(2, "0")}:00`);
      arr.push(`${String(h).padStart(2, "0")}:30`);
    }
    return arr;
  }, [hallHours]);

  const slotsPerMatch = Math.ceil(matchDuration / 30);

  // Day matches filtered
  const dayMatches = useMemo(() => {
    let filtered = weekMatches.filter((m) => m.scheduled_date === dateStr);
    if (divisionFilter) {
      filtered = filtered.filter((m) => m.division_id === divisionFilter);
    }
    return filtered;
  }, [weekMatches, dateStr, divisionFilter]);

  // Day bookings
  const dayBookings = useMemo(
    () =>
      bookings.filter((b) => {
        const bDate = new Date(b.start_time);
        return isSameDay(bDate, currentDate);
      }),
    [bookings, currentDate],
  );

  // ── Day-view auto-scroll to evening (TASK-74 SCH-A) ──────────────────
  //
  // The day-view grid spans hallHours.start → hallHours.end (e.g. 05:00 →
  // 24:00 = 38 half-hour columns × 48px = 1824px wide). Without this hook
  // the viewport defaults to 05:00 and admins have to scroll right ~24
  // columns to find the league matches that sit at 17:30+ — see
  // audit-reports/league-schedule-generation/05-schemavy-audit.md (F1).
  //
  // Behavior: on first render of a given (viewMode, date) we set
  // viewport.scrollLeft to the position of the earliest dayMatches time
  // (or 17:00 fallback when no matches exist), with a 2-slot lead-in for
  // context. We key the guard on viewMode + dateStr so changing day or
  // toggling week↔day re-arms the auto-scroll, but normal re-renders
  // (and user manual scrolls) don't fight the user's chosen position.
  const dayScrollAreaRef = useRef<HTMLDivElement>(null);
  const lastAutoScrolledKeyRef = useRef<string>("");

  useEffect(() => {
    if (viewMode !== "day") return;
    if (isLoading) return;

    const key = `${viewMode}|${dateStr}`;
    if (lastAutoScrolledKeyRef.current === key) return;

    const root = dayScrollAreaRef.current;
    if (!root) return;
    const viewport = root.querySelector<HTMLDivElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (!viewport) return;

    const matchTimes = dayMatches
      .map((m) => m.scheduled_time)
      .filter((t): t is string => Boolean(t));

    viewport.scrollLeft = calculateEveningScrollPosition({
      matchTimes,
      hallStartHour: hallHours.start,
      slotWidth: SLOT_W,
      fallbackHour: 17,
      leadInSlots: 2,
    });

    lastAutoScrolledKeyRef.current = key;
  }, [viewMode, isLoading, dateStr, dayMatches, hallHours.start]);

  // ── Slot helpers ─────────────────────────────────────────────────────

  const getMatchAt = useCallback(
    (courtId: string, time: string): CalendarMatch | undefined => {
      return dayMatches.find(
        (m) => m.court_id === courtId && m.scheduled_time === time,
      );
    },
    [dayMatches],
  );

  const isMatchMidSlot = useCallback(
    (courtId: string, time: string): boolean => {
      const slotMin = timeToMinutes(time);
      return dayMatches.some((m) => {
        if (m.court_id !== courtId || !m.scheduled_time) return false;
        const mStart = timeToMinutes(m.scheduled_time);
        return slotMin > mStart && slotMin < mStart + matchDuration;
      });
    },
    [dayMatches, matchDuration],
  );

  const isBookingAt = useCallback(
    (courtId: string, time: string): boolean => {
      const slotStart = timeToMinutes(time);
      const slotEnd = slotStart + 30;
      return dayBookings.some((b) => {
        if (b.court_id !== courtId) return false;
        const bStart = new Date(b.start_time);
        const bEnd = new Date(b.end_time);
        const bStartMin = bStart.getHours() * 60 + bStart.getMinutes();
        const bEndMin = bEnd.getHours() * 60 + bEnd.getMinutes();
        return slotStart < bEndMin && slotEnd > bStartMin;
      });
    },
    [dayBookings],
  );

  const isZoneCovered = useCallback(
    (courtId: string, time: string): LeagueAvailabilityZone | undefined => {
      const jsDay = currentDate.getDay();
      const isoDay = jsDay === 0 ? 7 : jsDay;
      const slotMin = timeToMinutes(time);
      return zones.find((z) => {
        if (!z.day_of_week.includes(isoDay)) return false;
        if (!z.court_ids.includes(courtId)) return false;
        if (z.valid_from && dateStr < z.valid_from) return false;
        if (z.valid_to && dateStr > z.valid_to) return false;
        const zStart = timeToMinutes(z.start_time);
        const zEnd = timeToMinutes(z.end_time);
        return slotMin >= zStart && slotMin < zEnd;
      });
    },
    [zones, currentDate, dateStr],
  );

  const isSlotOccupied = useCallback(
    (courtId: string, time: string): boolean => {
      return isBookingAt(courtId, time) || !!getMatchAt(courtId, time) || isMatchMidSlot(courtId, time);
    },
    [isBookingAt, getMatchAt, isMatchMidSlot],
  );

  // Booking block dimensions
  const getBookingSpan = useCallback(
    (courtId: string, time: string): { isFirst: boolean; slots: number } | null => {
      const slotMin = timeToMinutes(time);
      const booking = dayBookings.find((b) => {
        if (b.court_id !== courtId) return false;
        const bStart = new Date(b.start_time);
        const bStartMin = bStart.getHours() * 60 + bStart.getMinutes();
        return bStartMin === slotMin;
      });
      if (!booking) return null;
      const bEnd = new Date(booking.end_time);
      const bEndMin = bEnd.getHours() * 60 + bEnd.getMinutes();
      const slots = Math.ceil((bEndMin - slotMin) / 30);
      return { isFirst: true, slots };
    },
    [dayBookings],
  );

  // ── Navigation ───────────────────────────────────────────────────────

  const goToToday = () => {
    const now = new Date();
    setWeekStart(startOfWeek(now, { weekStartsOn: 1 }));
    const d = now.getDay();
    setSelectedDayIdx(d === 0 ? 6 : d - 1);
  };

  // ── Unplaced matches filtered ────────────────────────────────────────

  const filteredUnplaced = useMemo(() => {
    if (!divisionFilter) return unplacedMatches;
    return unplacedMatches.filter((g) => g.division_id === divisionFilter);
  }, [unplacedMatches, divisionFilter]);

  const totalUnplaced = useMemo(
    () => unplacedMatches.reduce((s, g) => s + g.matches.length, 0),
    [unplacedMatches],
  );

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <div className="space-y-3">
      {/* Week navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setWeekStart((d) => addDays(d, -7))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium px-2 min-w-[100px] text-center">
            v.{weekNum}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => setWeekStart((d) => addDays(d, 7))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={goToToday}>
            <CalendarDays className="h-4 w-4 mr-1" />
            {t("leagues.calendar.today")}
          </Button>
          <Button
            variant={viewMode === 'week' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('week')}
          >
            {t("leagues.calendar.week")}
          </Button>
          <Button
            variant={viewMode === 'day' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('day')}
          >
            {t("leagues.calendar.day")}
          </Button>
        </div>
      </div>

      {/* Day tabs */}
      <div className="flex gap-1">
        {weekDays.map((day, idx) => {
          const isToday = isSameDay(day, new Date());
          const dayMatchCount = weekMatches.filter(
            (m) => m.scheduled_date === format(day, "yyyy-MM-dd"),
          ).length;
          return (
            <button
              key={idx}
              onClick={() => { setSelectedDayIdx(idx); setViewMode('day'); }}
              className={cn(
                "flex-1 rounded-md px-1 py-1.5 text-center text-xs transition-colors",
                viewMode === 'day' && selectedDayIdx === idx
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted",
                isToday && !(viewMode === 'day' && selectedDayIdx === idx) && "ring-1 ring-primary/40",
              )}
            >
              <div className="font-medium">{t(DAY_LABEL_KEYS[idx])}</div>
              <div className="text-[10px] opacity-70">
                {format(day, "d/M")}
              </div>
              {dayMatchCount > 0 && (
                <div className="text-[9px] mt-0.5 opacity-60">
                  {dayMatchCount} matcher
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Division filter + mode toggle */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        {divisions.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            <Badge
              variant={!divisionFilter ? "default" : "outline"}
              className="cursor-pointer text-xs"
              onClick={() => setDivisionFilter(null)}
            >
              {t("leagues.all")}
            </Badge>
            {divisions.map((d) => {
              const c = getDivisionColor(d.level);
              return (
                <Badge
                  key={d.id}
                  variant={divisionFilter === d.id ? "default" : "outline"}
                  className="cursor-pointer text-xs"
                  style={{
                    backgroundColor: divisionFilter === d.id ? c.hex : undefined,
                    color: divisionFilter === d.id ? 'white' : undefined,
                    borderColor: divisionFilter !== d.id ? c.hex : undefined,
                  }}
                  onClick={() =>
                    setDivisionFilter(divisionFilter === d.id ? null : d.id)
                  }
                >
                  {d.name}
                </Badge>
              );
            })}
          </div>
        )}

        {enableZoneMode && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Läge:</span>
            <button
              onClick={() => setCalendarMode('schedule')}
              className={cn(
                "px-2 py-0.5 rounded",
                calendarMode === 'schedule'
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted",
              )}
            >
              {t("leagues.calendar.scheduleMatches")}
            </button>
            <button
              onClick={() => setCalendarMode('zones')}
              className={cn(
                "px-2 py-0.5 rounded",
                calendarMode === 'zones'
                  ? "bg-green-600 text-white"
                  : "hover:bg-muted",
              )}
            >
              {t("leagues.calendar.markFreeZones")}
            </button>
          </div>
        )}
      </div>

      {/* Calendar grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
          {t("leagues.loading")}
        </div>
      ) : viewMode === 'week' ? (
        /* ── Week overview grid ──────────────────────────────────── */
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr>
                <th className="text-left px-2 py-1 text-muted-foreground font-medium" style={{ width: COURT_LABEL_W }}>{t("leagues.calendar.court")}</th>
                {weekDays.map((day, idx) => {
                  const dayStr = format(day, "yyyy-MM-dd");
                  const matchCount = weekMatches.filter((m) => m.scheduled_date === dayStr && (!divisionFilter || m.division_id === divisionFilter)).length;
                  const isToday = isSameDay(day, new Date());
                  return (
                    <th
                      key={idx}
                      className={cn(
                        "text-center px-1 py-1 cursor-pointer hover:bg-muted/50 transition-colors font-medium",
                        isToday && "bg-primary/5",
                      )}
                      onClick={() => { setSelectedDayIdx(idx); setViewMode('day'); }}
                    >
                      <div>{t(DAY_LABEL_KEYS[idx])}</div>
                      <div className="text-[10px] font-normal opacity-70">{format(day, "d/M")}</div>
                      {matchCount > 0 && <div className="text-[9px] font-normal opacity-60">{matchCount}</div>}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {courts.map((court) => (
                <tr key={court.id} className="border-t">
                  <td className="px-2 py-2 font-medium text-muted-foreground whitespace-nowrap">{court.name}</td>
                  {weekDays.map((day, dayIdx) => {
                    const dayStr = format(day, "yyyy-MM-dd");
                    const cellMatches = weekMatches.filter(
                      (m) => m.scheduled_date === dayStr && m.court_id === court.id && (!divisionFilter || m.division_id === divisionFilter),
                    );
                    const cellBookings = bookings.filter((b) => {
                      const bDate = new Date(b.start_time);
                      return isSameDay(bDate, day) && b.court_id === court.id;
                    });
                    return (
                      <td
                        key={dayIdx}
                        className="text-center px-1 py-2 cursor-pointer hover:bg-muted/30 transition-colors"
                        onClick={() => { setSelectedDayIdx(dayIdx); setViewMode('day'); }}
                      >
                        <div className="flex flex-wrap gap-0.5 justify-center">
                          {cellBookings.map((b) => (
                            <span key={b.id} className="inline-block h-2.5 w-2.5 rounded-sm bg-gray-300/60" title="Bokning" />
                          ))}
                          {cellMatches.map((m) => {
                            const c = getDivisionColor(m.division_level);
                            return (
                              <span
                                key={m.id}
                                className="inline-block h-2.5 w-2.5 rounded-sm"
                                style={{ backgroundColor: c.hex }}
                                title={`D${m.division_level}: ${m.home_label} vs ${m.away_label}`}
                              />
                            );
                          })}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* ── Day view grid ───────────────────────────────────────── */
        <ScrollArea ref={dayScrollAreaRef} className="w-full">
          <div className="relative" style={{ minWidth: COURT_LABEL_W + timeSlots.length * SLOT_W }}>
            {/* Header: time labels */}
            <div className="flex sticky top-0 z-10 bg-background border-b">
              <div
                className="shrink-0 text-xs font-medium text-muted-foreground flex items-end pb-1 px-2"
                style={{ width: COURT_LABEL_W }}
              >
                {format(currentDate, "EEEE d MMMM", { locale: sv })}
              </div>
              {timeSlots.map((slot) => (
                <div
                  key={slot}
                  className="text-[10px] text-muted-foreground text-center border-l pb-1 flex items-end justify-center"
                  style={{ width: SLOT_W }}
                >
                  {slot.endsWith(":00") ? slot : ""}
                </div>
              ))}
            </div>

            {/* Court rows */}
            {courts.map((court) => (
              <div key={court.id} className="flex border-b hover:bg-muted/20">
                {/* Court label */}
                <div
                  className="shrink-0 text-xs font-medium flex items-center px-2 border-r bg-background sticky left-0 z-[5]"
                  style={{ width: COURT_LABEL_W, height: SLOT_H }}
                >
                  {court.name}
                </div>

                {/* Time slots */}
                <div className="flex relative" style={{ height: SLOT_H }}>
                  {timeSlots.map((slot, _slotIdx) => {
                    const match = getMatchAt(court.id, slot);
                    const isMid = isMatchMidSlot(court.id, slot);
                    const bookingInfo = getBookingSpan(court.id, slot);
                    const hasBooking = isBookingAt(court.id, slot);
                    const zone = isZoneCovered(court.id, slot);
                    const occupied = isSlotOccupied(court.id, slot);

                    // Render match block
                    if (match) {
                      const divColor = getDivisionColor(match.division_level);
                      return (
                        <div
                          key={slot}
                          className="relative"
                          style={{ width: SLOT_W }}
                        >
                          <MatchBlock
                            match={match}
                            divColor={divColor}
                            slotsPerMatch={slotsPerMatch}
                            slotWidth={SLOT_W}
                            slotHeight={SLOT_H}
                            readOnly={readOnly}
                            onUnplace={() => unplaceMatch.mutate(match.id)}
                          />
                        </div>
                      );
                    }

                    // Skip rendering for mid-match slots
                    if (isMid) {
                      return (
                        <div
                          key={slot}
                          className="border-l border-transparent"
                          style={{ width: SLOT_W }}
                        />
                      );
                    }

                    // Render booking block
                    if (bookingInfo?.isFirst) {
                      return (
                        <div
                          key={slot}
                          className="relative border-l"
                          style={{ width: SLOT_W }}
                        >
                          <div
                            className="absolute inset-y-1 left-0.5 rounded bg-gray-300/30 cursor-not-allowed flex items-center justify-center"
                            style={{
                              width: bookingInfo.slots * SLOT_W - 4,
                              zIndex: 2,
                            }}
                          >
                            <span className="text-[9px] text-muted-foreground truncate px-1">
                              {t("leagues.calendar.booked")}
                            </span>
                          </div>
                        </div>
                      );
                    }

                    if (hasBooking && !bookingInfo?.isFirst) {
                      return (
                        <div
                          key={slot}
                          className="border-l border-transparent"
                          style={{ width: SLOT_W }}
                        />
                      );
                    }

                    // Empty slot — clickable if not occupied
                    return (
                      <EmptySlot
                        key={slot}
                        courtId={court.id}
                        courtName={court.name}
                        time={slot}
                        dateStr={dateStr}
                        zone={zone}
                        occupied={occupied}
                        unplacedMatches={filteredUnplaced}
                        readOnly={readOnly}
                        calendarMode={enableZoneMode ? calendarMode : 'schedule'}
                        divisions={divisions}
                        leagueId={leagueId}
                        onPlace={(matchId) =>
                          placeMatch.mutate({
                            matchId,
                            courtId: court.id,
                            date: dateStr,
                            time: slot,
                          })
                        }
                        onCreateZone={(zone) => createZone.mutate(zone)}
                        onCreateSingleSlot={async (slot) => {
                          const { error } = await (supabase as any)
                            .from("league_available_slots")
                            .insert(slot);
                          if (error) {
                            toast.error(error.message || t("leagues.calendar.couldNotCreateZone"));
                          } else {
                            toast.success(t("leagues.calendar.zoneCreated"));
                            queryClient.invalidateQueries({ queryKey: ["league-availability-zones", leagueId] });
                          }
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-3 px-2">
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-gray-300/50" />
          {t("leagues.calendar.existingBooking")}
        </span>
        {divisions.map((d) => {
          const c = getDivisionColor(d.level);
          return (
            <span key={d.id} className="flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: c.hex }} />
              {d.name}
            </span>
          );
        })}
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm border-2 border-dashed border-orange-400 bg-orange-50" />
          {t("leagues.calendar.extraTime")}
        </span>
      </div>

      {/* Unplaced matches panel (hidden in readOnly) */}
      {!readOnly && totalUnplaced > 0 && (
        <Collapsible open={unplacedOpen} onOpenChange={setUnplacedOpen}>
          <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full [&[data-state=open]>svg:last-child]:rotate-180">
            <Badge variant="secondary" className="text-xs">
              {totalUnplaced}
            </Badge>
            <span>{t("leagues.calendar.unplacedMatches")}</span>
            <ChevronDown className="h-4 w-4 ml-auto transition-transform duration-200" />
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2">
            <div className="space-y-2">
              {filteredUnplaced.map((group) => (
                <div
                  key={`${group.division_id}-${group.round}`}
                  className="text-xs"
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <div
                      className="w-2.5 h-2.5 rounded-sm"
                      style={{ backgroundColor: getDivisionColor(group.division_level).hex }}
                    />
                    <span className="font-medium">
                      {group.division_name} &middot; Omg {group.round}
                    </span>
                    <Badge variant="outline" className="text-[10px] px-1 h-4">
                      {group.matches.length}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-1 pl-4">
                    {group.matches.map((m) => (
                      <div
                        key={m.id}
                        className="text-[11px] text-muted-foreground truncate"
                      >
                        {m.home_label} vs {m.away_label}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────

function MatchBlock({
  match,
  divColor,
  slotsPerMatch,
  slotWidth,
  slotHeight: _slotHeight,
  readOnly = false,
  onUnplace,
}: {
  match: CalendarMatch;
  divColor: { bg: string; hex: string; label: string };
  slotsPerMatch: number;
  slotWidth: number;
  slotHeight: number;
  readOnly?: boolean;
  onUnplace: () => void;
}) {
  // PR-AV: useTranslation must be called at component scope; previously
  // `t` was used without being destructured here (silently undefined under
  // tsconfig strict:false) — TS2304 "Cannot find name 't'".
  const { t } = useTranslation("admin");
  const divLabel = `D${match.division_level}`;
  const fullTitle = `${divLabel} · ${match.home_label} vs ${match.away_label} · Omg ${match.round}`;
  const isNarrow = slotsPerMatch < 3;

  const blockStyle: React.CSSProperties = {
    width: slotsPerMatch * slotWidth - 4,
    ...(divColor.bg ? {} : { backgroundColor: divColor.hex }),
  };

  const blockClass = cn(
    "absolute inset-y-1 left-0.5 rounded text-white text-[10px] leading-tight p-1 overflow-hidden text-left z-[3]",
    divColor.bg || '',
    !readOnly && "cursor-pointer hover:brightness-110 transition-all",
  );

  const blockInner = isNarrow ? (
    <div className="font-medium truncate">{divLabel}</div>
  ) : (
    <>
      <div className="font-medium truncate">
        {divLabel} · {match.home_label} vs {match.away_label}
      </div>
      <div className="truncate opacity-80">Omg {match.round}</div>
    </>
  );

  if (readOnly) {
    return (
      <div className={blockClass} style={blockStyle} title={fullTitle}>
        {blockInner}
      </div>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={blockClass} style={blockStyle} title={fullTitle}>
          {blockInner}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3" side="top">
        <div className="space-y-2">
          <div className="text-xs">
            <span className="font-medium">{match.division_name}</span>
            {" "}— Omgång {match.round}
          </div>
          <div className="text-sm font-medium">
            {match.home_label} vs {match.away_label}
          </div>
          <div className="text-xs text-muted-foreground">
            {match.scheduled_date} kl {match.scheduled_time}
          </div>
          <Button
            variant="destructive"
            size="sm"
            className="w-full text-xs"
            onClick={onUnplace}
          >
            <X className="h-3 w-3 mr-1" />
            {t("leagues.calendar.removePlacement")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function EmptySlot({
  courtId,
  courtName,
  time,
  dateStr,
  zone,
  occupied,
  unplacedMatches,
  readOnly = false,
  calendarMode = 'schedule',
  divisions = [],
  leagueId,
  onPlace,
  onCreateZone,
  onCreateSingleSlot,
}: {
  courtId: string;
  courtName?: string;
  time: string;
  dateStr: string;
  zone: LeagueAvailabilityZone | undefined;
  occupied: boolean;
  unplacedMatches: Array<{
    division_id: string;
    division_name: string;
    division_level: number;
    round: number;
    matches: CalendarMatch[];
  }>;
  readOnly?: boolean;
  calendarMode?: 'schedule' | 'zones';
  divisions?: Array<{ id: string; name: string; level: number }>;
  leagueId?: string;
  onPlace: (matchId: string) => void;
  onCreateZone?: (zone: any) => void;
  onCreateSingleSlot?: (slot: any) => void;
}) {
  // PR-AV: useTranslation must be called at component scope (TS2304 fix).
  const { t } = useTranslation("admin");
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [selectedMatchId, setSelectedMatchId] = useState<string>("");
  const [zoneDivisionScope, setZoneDivisionScope] = useState<string>("all");
  const [zoneRecurring, setZoneRecurring] = useState(false);

  const groupOptions = unplacedMatches.map((g) => ({
    value: `${g.division_id}__${g.round}`,
    label: `${g.division_name} — Omg ${g.round}`,
    matches: g.matches,
  }));

  const currentGroupMatches =
    groupOptions.find((g) => g.value === selectedGroup)?.matches ?? [];

  if (occupied) {
    return (
      <div
        className="border-l border-dashed border-muted-foreground/20"
        style={{ width: SLOT_W }}
      />
    );
  }

  if (readOnly) {
    return (
      <div
        className={cn(
          "border-l border-dashed border-muted-foreground/20",
          zone && "border-green-500 border-dashed border-2 bg-green-50/30",
        )}
        style={{ width: SLOT_W, height: SLOT_H }}
        title={zone ? `Frizon: ${zone.name}` : `${dateStr} ${time}`}
      />
    );
  }

  // Zone mode popover content
  if (calendarMode === 'zones') {
    const slotDate = new Date(dateStr + "T00:00:00");
    const jsDay = slotDate.getDay();
    const isoDay = jsDay === 0 ? 7 : jsDay;
    const endMinutes = timeToMinutes(time) + 30;
    const endTime = minutesToTime(endMinutes);

    return (
      <Popover
        onOpenChange={() => {
          setZoneDivisionScope("all");
          setZoneRecurring(false);
        }}
      >
        <PopoverTrigger asChild>
          <button
            className={cn(
              "border-l border-dashed border-muted-foreground/20 hover:bg-green-50 transition-colors",
              zone && "border-green-500 border-dashed border-2 bg-green-50/30",
            )}
            style={{ width: SLOT_W, height: SLOT_H }}
            title={zone ? `Frizon: ${zone.name}` : `${dateStr} ${time} — Markera som frizon`}
          />
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3" side="top">
          <div className="space-y-3">
            <div className="text-xs font-medium text-green-700">{t("leagues.calendar.markAsFreeZone")}</div>
            <div className="text-xs text-muted-foreground">
              {dateStr} kl {time}–{endTime} &middot; {courtName || courtId.slice(0, 8)}
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium">{t("leagues.calendar.appliesTo")}:</label>
              <Select value={zoneDivisionScope} onValueChange={setZoneDivisionScope}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs">{t("leagues.calendar.allDivisions")}</SelectItem>
                  {divisions.map((d) => (
                    <SelectItem key={d.id} value={d.id} className="text-xs">
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id={`recurring-${courtId}-${time}`}
                checked={zoneRecurring}
                onCheckedChange={setZoneRecurring}
              />
              <Label htmlFor={`recurring-${courtId}-${time}`} className="text-xs">
                {t("leagues.calendar.makeRecurring")}
              </Label>
            </div>

            <Button
              size="sm"
              className="w-full text-xs bg-green-600 hover:bg-green-700"
              onClick={() => {
                if (zoneRecurring && onCreateZone && leagueId) {
                  onCreateZone({
                    league_id: leagueId,
                    name: `Frizon ${courtName || courtId.slice(0, 8)} ${time}`,
                    court_ids: [courtId],
                    day_of_week: [isoDay],
                    start_time: time,
                    end_time: endTime,
                    valid_from: null,
                    valid_to: null,
                  });
                } else if (onCreateSingleSlot && leagueId) {
                  onCreateSingleSlot({
                    league_id: leagueId,
                    court_id: courtId,
                    date: dateStr,
                    start_time: time,
                    end_time: endTime,
                  });
                }
              }}
            >
              {zoneRecurring ? t("leagues.calendar.createRecurringZone") : t("leagues.calendar.markAsFreeZone")}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // Schedule mode popover content (existing behavior)
  return (
    <Popover
      onOpenChange={() => {
        setSelectedGroup("");
        setSelectedMatchId("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          className={cn(
            "border-l border-dashed border-muted-foreground/20 hover:bg-primary/5 transition-colors",
            zone && "border-green-500 border-dashed border-2 bg-green-50/30",
          )}
          style={{ width: SLOT_W, height: SLOT_H }}
          title={zone ? `Frizon: ${zone.name}` : `${dateStr} ${time}`}
        />
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" side="top">
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">
            {dateStr} kl {time} — {courtId.slice(0, 8)}...
          </div>

          {groupOptions.length === 0 ? (
            <div className="text-xs text-muted-foreground">
              {t("leagues.calendar.noUnplacedMatches")}
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <label className="text-xs font-medium">Division / Omgång</label>
                <Select value={selectedGroup} onValueChange={(v) => { setSelectedGroup(v); setSelectedMatchId(""); }}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder={t("leagues.calendar.select")} />
                  </SelectTrigger>
                  <SelectContent>
                    {groupOptions.map((g) => (
                      <SelectItem key={g.value} value={g.value} className="text-xs">
                        {g.label} ({g.matches.length})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {currentGroupMatches.length > 0 && (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium">Match</label>
                  <Select value={selectedMatchId} onValueChange={setSelectedMatchId}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder={t("leagues.calendar.selectMatch")} />
                    </SelectTrigger>
                    <SelectContent>
                      {currentGroupMatches.map((m) => (
                        <SelectItem key={m.id} value={m.id} className="text-xs">
                          {m.home_label} vs {m.away_label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button
                size="sm"
                className="w-full text-xs"
                disabled={!selectedMatchId}
                onClick={() => {
                  if (selectedMatchId) {
                    onPlace(selectedMatchId);
                  }
                }}
              >
                {t("leagues.calendar.place")}
              </Button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
